import { TopicsScores } from '@/domain/impact-calculate/entities/calculate.entity';
import { ModelVersionESS } from '@/domain/project/entities/project.entity';
import { VenueDate } from '@/domain/project-venue-dates/entities/project-venue-dates.entity';
import { formatDateToYearMonth } from '@/domain/utils/format-date-to-year-month';

import { VenueDatesMonthView } from './VenueDatesMonthView';

export function VenueDatesMonthListView({
  venueDates,
  scoresByMonth,
  scoresByDates,
  onClickAdd,
  onClickEditDay,
  onClickEditMonth,
  onClickDelete,
  onClickPresets,
  onSelectPreset,
  isSelecting,
  selectedIds,
  onToggleSelect,
  onToggleSelectMonth,
}: {
  venueDates: VenueDate[];
  scoresByMonth: Record<string, TopicsScores>;
  scoresByDates: Record<number, TopicsScores>;
  onClickAdd: (month: string) => void;
  onClickEditDay: (
    id: number,
    modelESS: ModelVersionESS,
    scrollTo?: string,
  ) => void;
  onClickEditMonth: (monthDate: string) => void;
  onClickDelete: (id: number) => void;
  onClickPresets: () => void;
  onSelectPreset: (venueDateId: number, presetId: number) => void;
  isSelecting: boolean;
  selectedIds: Set<number>;
  onToggleSelect: (id: number) => void;
  onToggleSelectMonth: (ids: number[], allSelected: boolean) => void;
}) {
  const groupedVenueDates = groupVenueDatesByMonth(venueDates);
  sortDates(groupedVenueDates);
  return (
    <>
      {groupedVenueDates.map((monthVenueDates) => {
        const monthIds = monthVenueDates.venueDates.map((d) => d.id);
        const allSelectedInMonth = monthIds.every((id) => selectedIds.has(id));
        const someSelectedInMonth = monthIds.some((id) => selectedIds.has(id));
        const onToggleAllForMonth = () =>
          onToggleSelectMonth(monthIds, allSelectedInMonth);
        return (
          <VenueDatesMonthView
            key={monthVenueDates.monthDate}
            monthDate={monthVenueDates.monthDate}
            venueDates={monthVenueDates.venueDates}
            scoresByMonth={scoresByMonth}
            scoresByDates={scoresByDates}
            onClickAdd={() => onClickAdd(monthVenueDates.monthDate)}
            onClickEditDay={onClickEditDay}
            onClickEditMonth={onClickEditMonth}
            onClickDelete={onClickDelete}
            onClickPresets={onClickPresets}
            onSelectPreset={onSelectPreset}
            isSelecting={isSelecting}
            selectedIds={selectedIds}
            onToggleSelect={onToggleSelect}
            allSelectedInMonth={allSelectedInMonth}
            someSelectedInMonth={someSelectedInMonth}
            onToggleAllForMonth={onToggleAllForMonth}
          />
        );
      })}
    </>
  );
}

type GroupedVenueDatesByMonth = {
  monthDate: string;
  venueDates: VenueDate[];
}[];

function groupVenueDatesByMonth(venueDates: VenueDate[]) {
  const venueDatesByMonth = venueDates
    .reduce<GroupedVenueDatesByMonth>(reduceGroupVenueDatesByMonth, [])
    .sort(sortMonths);
  return venueDatesByMonth;
}

function reduceGroupVenueDatesByMonth(
  acc: GroupedVenueDatesByMonth,
  venueDate: VenueDate,
) {
  const date = new Date(venueDate.date);
  const monthKey = formatDateToYearMonth(date);
  const monthObject = acc.find((item) => item.monthDate === monthKey);
  if (monthObject) {
    monthObject.venueDates.push(venueDate);
  } else {
    acc.push({ monthDate: monthKey, venueDates: [venueDate] });
  }
  return acc;
}

function sortMonths(
  a: {
    monthDate: string;
    venueDates: VenueDate[];
  },
  b: {
    monthDate: string;
    venueDates: VenueDate[];
  },
): number {
  return new Date(a.monthDate).getTime() - new Date(b.monthDate).getTime();
}

function sortDates(groupedVenueDates: GroupedVenueDatesByMonth) {
  groupedVenueDates.forEach((monthVenueDates) => {
    monthVenueDates.venueDates.sort((a, b) => {
      return new Date(a.date).getTime() - new Date(b.date).getTime();
    });
  });
}
